// ============================================================
// 鉴权 store（token + 用户信息 + 角色 / 权限码）
// 各端独立 pinia 实例 + storage 命名空间，三端登录态完全隔离
// 与各端注入的 loginApi / logoutApi 解耦：
//   - 业务侧调用 useAuthStore().setLoginResult({ token, userInfo })
//   - 守卫侧调用 useAuthStore().fetchInfoIfNeed() 拉取用户信息
//   - 登出由 useAuthStore().logout() 统一清理
// ============================================================
import { defineStore } from "pinia";
import { getToken, removeToken, setToken, getUserInfoCache, setUserInfoCache, removeUserInfoCache } from "@shared/utils/auth";
import type { UserInfo } from "@shared/types/user";

interface AuthState {
    token: string;
    userInfo: UserInfo | null;
}

export const useAuthStore = defineStore("auth", {
    state: (): AuthState => ({
        token: getToken(),
        userInfo: getUserInfoCache<UserInfo>(),
    }),

    getters: {
        isLogin: state => !!state.token,
        name: state => state.userInfo?.name || "",
        avatar: state => state.userInfo?.avatar || "",
        roles: (state): string[] => state.userInfo?.roles || [],
    },

    actions: {
        setToken(token: string) {
            this.token = token;
            setToken(token);
        },

        setUserInfo(info: UserInfo) {
            this.userInfo = info;
            setUserInfoCache(info);
        },

        /** 登录接口返回后一次性塞入 token 和（可选的）userInfo */
        setLoginResult(payload: { token: string; userInfo?: UserInfo }) {
            this.setToken(payload.token);
            if (payload.userInfo) this.setUserInfo(payload.userInfo);
        },

        /** 清理本端登录态（token 失效 / 主动登出） */
        resetState() {
            this.token = "";
            this.userInfo = null;
            removeToken();
            removeUserInfoCache();
        },

        /** 业务侧统一登出入口；具体的远端 logout 请求由各端自己在调用前发起 */
        async logout() {
            const { useTagsViewStore } = await import("@shared/store/modules/tagsView");
            useTagsViewStore().resetTags();
            this.resetState();
        },
    },
});
